/**
 * WomenOnlyOnboardingModal — Premium compact welcome for Ladies Hub
 *
 * - Shows only on first entry (or when policy version changes)
 * - Compact: title, value line, 3 benefits, 1 summary rule, CTAs
 * - Full guidelines in expandable bottom sheet
 * - CTA visible above fold — no forced scrolling
 * - Bilingual EN / AR
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Sparkles, ShieldCheck, Heart, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Ban, FileText,
} from 'lucide-react';

/* ── Policy version — bump this when guidelines materially change ─── */
const POLICY_VERSION = 2;
const STORAGE_KEY = 'ladies_hub_accepted';

/* ── Persistence helpers ─────────────────────────────────────────── */
export function checkLadiesHubVerified() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    return data.accepted === true && data.policyVersion === POLICY_VERSION;
  } catch {
    return false;
  }
}

export function setLadiesHubVerified(accepted = true) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        accepted,
        policyVersion: POLICY_VERSION,
        timestamp: new Date().toISOString(),
      })
    );
  } catch {}
}

/* ── Copy ─────────────────────────────────────────────────────────── */
const COPY = {
  en: {
    title: 'Welcome to Ladies Hub',
    subtitle:
      'A trusted space for women-led products, services, and creativity.',
    benefits: [
      { icon: Sparkles, text: 'Discover products & services by women sellers' },
      { icon: Heart, text: 'A safe, respectful community for all' },
      { icon: ShieldCheck, text: 'We review reports and act on violations' },
    ],
    rulesShort: 'No spam, scams, fake photos, or prohibited items.',
    ctaContinue: 'Continue to Ladies Hub',
    ctaNotNow: 'Not now',
    ctaGuidelines: 'View full guidelines',
    agreement:
      'I agree to follow Ladies Hub guidelines and help keep this space respectful and safe.',
    footer: 'By continuing you accept our community guidelines.',

    // Full guidelines (expandable)
    gl_allowed_title: 'What you can post',
    gl_allowed: [
      'Fashion, beauty, jewellery & accessories',
      'Home businesses & handmade crafts',
      'Services — tutoring, design, events, etc.',
      'Pre-loved items in good condition',
    ],
    gl_notallowed_title: 'Not allowed',
    gl_notallowed: [
      'Prohibited or illegal items',
      'Misleading descriptions or fake photos',
      'Spam, duplicate ads, or scams',
      'Harassment or inappropriate content',
    ],
    gl_safety_title: 'Tips',
    gl_safety: [
      'Add WhatsApp to sell faster',
      'Include your location for local buyers',
      'Report suspicious ads — we act quickly',
    ],
  },
  ar: {
    title: 'مرحباً بكِ في قسم السيدات',
    subtitle: 'مساحة موثوقة للمنتجات والخدمات والإبداع بقيادة نسائية.',
    benefits: [
      { icon: Sparkles, text: 'اكتشفي منتجات وخدمات من بائعات' },
      { icon: Heart, text: 'مجتمع آمن ومحترم للجميع' },
      { icon: ShieldCheck, text: 'نراجع البلاغات ونتعامل مع المخالفات' },
    ],
    rulesShort: 'ممنوع: إزعاج، احتيال، صور مزيفة، أو منتجات محظورة.',
    ctaContinue: 'الدخول إلى قسم السيدات',
    ctaNotNow: 'ليس الآن',
    ctaGuidelines: 'عرض الإرشادات الكاملة',
    agreement:
      'أوافق على الالتزام بإرشادات قسم السيدات والحفاظ على هذه المساحة آمنة ومحترمة.',
    footer: 'بالمتابعة توافقين على إرشادات المجتمع.',

    gl_allowed_title: 'ما يمكنك نشره',
    gl_allowed: [
      'أزياء، جمال، مجوهرات وإكسسوارات',
      'مشاريع منزلية وحرف يدوية',
      'خدمات — دروس، تصميم، مناسبات، إلخ',
      'منتجات مستعملة بحالة جيدة',
    ],
    gl_notallowed_title: 'غير مسموح',
    gl_notallowed: [
      'منتجات محظورة أو غير قانونية',
      'أوصاف مضللة أو صور مزيفة',
      'إزعاج أو إعلانات مكررة أو احتيال',
      'تحرش أو محتوى غير لائق',
    ],
    gl_safety_title: 'نصائح',
    gl_safety: [
      'أضيفي واتساب للبيع أسرع',
      'أضيفي موقعك للمشترين المحليين',
      'بلّغي عن إعلانات مشبوهة — نتعامل بسرعة',
    ],
  },
};

/* ── Colors ────────────────────────────────────────────────────────── */
const C = {
  gold: '#D4A843',
  goldLight: 'rgba(212,168,67,0.10)',
  goldBorder: 'rgba(212,168,67,0.25)',
  ink: '#1E293B',
  muted: '#64748B',
  surface: '#FFFFFF',
  dangerBg: '#FEF2F2',
  dangerBorder: '#FECACA',
  dangerText: '#991B1B',
  successBg: '#F0FDF4',
  successBorder: '#BBF7D0',
  successText: '#166534',
};

/* ── Component ─────────────────────────────────────────────────────── */
export default function WomenOnlyOnboardingModal({
  isOpen,
  onConfirm,
  onExplore,
  onExit,
  lang = 'en',
}) {
  const [showGuidelines, setShowGuidelines] = useState(false);
  const copy = COPY[lang] || COPY.en;
  const isRTL = lang === 'ar';

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleContinue = () => {
    setLadiesHubVerified(true);
    try { localStorage.setItem('hasSeenLadiesHubIntro', 'true'); } catch {}
    onConfirm?.();
  };

  const handleNotNow = () => {
    try { localStorage.setItem('hasSeenLadiesHubIntro', 'true'); } catch {}
    onExit?.();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="dialog"
          aria-modal="true"
          onClick={handleNotNow}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.50)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          data-testid="women-onboarding-backdrop"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="lh-welcome-card"
            style={{
              width: 'min(460px, 100%)',
              maxHeight: 'calc(100dvh - 32px)',
              overflow: 'auto',
              background: C.surface,
              borderRadius: 22,
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
              position: 'relative',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
            data-testid="women-onboarding-modal"
          >
            {/* ── Close ────────────────────────────────── */}
            <button
              onClick={handleNotNow}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: 14,
                ...(isRTL ? { left: 14 } : { right: 14 }),
                width: 32,
                height: 32,
                borderRadius: 10,
                border: '1px solid rgba(0,0,0,0.06)',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
              }}
              data-testid="women-onboarding-close"
            >
              <X size={16} color={C.muted} />
            </button>

            {/* ── Body ─────────────────────────────────── */}
            <div style={{ padding: '28px 22px 22px' }}>
              {/* Title + subtitle */}
              <h2
                style={{
                  margin: 0,
                  fontSize: 21,
                  fontWeight: 800,
                  color: C.ink,
                  letterSpacing: -0.3,
                  lineHeight: 1.25,
                  paddingInlineEnd: 36,
                }}
              >
                {copy.title}
              </h2>
              <p
                style={{
                  margin: '8px 0 0',
                  fontSize: 14,
                  color: C.gold,
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                {copy.subtitle}
              </p>

              {/* 3 benefit bullets */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  margin: '20px 0 0',
                }}
              >
                {copy.benefits.map((b, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 9,
                        background: C.goldLight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <b.icon size={15} color={C.gold} strokeWidth={2.2} />
                    </div>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: C.ink,
                        lineHeight: 1.35,
                      }}
                    >
                      {b.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Short "not allowed" summary */}
              <div
                style={{
                  margin: '16px 0 0',
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: C.dangerBg,
                  border: `1px solid ${C.dangerBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Ban size={14} color={C.dangerText} style={{ flexShrink: 0 }} />
                <span
                  style={{
                    fontSize: 13,
                    color: C.dangerText,
                    fontWeight: 500,
                    lineHeight: 1.35,
                  }}
                >
                  {copy.rulesShort}
                </span>
              </div>

              {/* Agreement line */}
              <p
                style={{
                  margin: '16px 0 0',
                  fontSize: 12.5,
                  color: C.muted,
                  lineHeight: 1.5,
                  textAlign: 'center',
                }}
              >
                {copy.agreement}
              </p>

              {/* ── CTAs ──────────────────────────────── */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  marginTop: 16,
                }}
              >
                {/* Primary */}
                <button
                  onClick={handleContinue}
                  style={{
                    height: 50,
                    borderRadius: 14,
                    border: 'none',
                    background: `linear-gradient(135deg, #F59E0B, #FB923C)`,
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: '0 6px 18px rgba(245,158,11,0.35)',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                  }}
                  data-testid="women-onboarding-confirm-btn"
                >
                  {copy.ctaContinue}
                  {isRTL ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                </button>

                {/* Secondary */}
                <button
                  onClick={handleNotNow}
                  style={{
                    height: 46,
                    borderRadius: 14,
                    border: '1px solid rgba(0,0,0,0.08)',
                    background: '#fff',
                    color: C.muted,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  data-testid="women-onboarding-exit-btn"
                >
                  {copy.ctaNotNow}
                </button>

                {/* View full guidelines */}
                <button
                  onClick={() => setShowGuidelines((v) => !v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    color: C.gold,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    marginTop: 2,
                  }}
                  data-testid="view-full-guidelines-btn"
                >
                  <FileText size={13} />
                  {copy.ctaGuidelines}
                  {showGuidelines ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </button>
              </div>

              {/* ── Expandable full guidelines ────────── */}
              <AnimatePresence>
                {showGuidelines && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    style={{ overflow: 'hidden', marginTop: 14 }}
                    data-testid="full-guidelines-section"
                  >
                    {/* Allowed */}
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: C.successBg,
                        border: `1px solid ${C.successBorder}`,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: C.successText,
                          marginBottom: 6,
                        }}
                      >
                        {copy.gl_allowed_title}
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingInlineStart: 18,
                          fontSize: 12.5,
                          color: C.successText,
                          lineHeight: 1.7,
                        }}
                      >
                        {copy.gl_allowed.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Not allowed */}
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: C.dangerBg,
                        border: `1px solid ${C.dangerBorder}`,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: C.dangerText,
                          marginBottom: 6,
                        }}
                      >
                        {copy.gl_notallowed_title}
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingInlineStart: 18,
                          fontSize: 12.5,
                          color: C.dangerText,
                          lineHeight: 1.7,
                        }}
                      >
                        {copy.gl_notallowed.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Safety tips */}
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: C.ink,
                          marginBottom: 6,
                        }}
                      >
                        {copy.gl_safety_title}
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingInlineStart: 18,
                          fontSize: 12.5,
                          color: C.muted,
                          lineHeight: 1.7,
                        }}
                      >
                        {copy.gl_safety.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer */}
              <p
                style={{
                  margin: '14px 0 0',
                  fontSize: 11.5,
                  color: '#94A3B8',
                  textAlign: 'center',
                  lineHeight: 1.4,
                }}
              >
                {copy.footer}
              </p>
            </div>
          </motion.div>

          <style>{`
            @supports not (height: 100dvh) {
              .lh-welcome-card { max-height: calc(100vh - 32px) !important; }
            }
            .lh-welcome-card::-webkit-scrollbar { width: 0; }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
